import { Button, Typography } from '@ori-ui/mui';
import { DynamicFormattedMessage } from '@ori/i18n';
import { getContactByType, usePersonalBeautyStore } from '@ori/personal-beauty-store';
import useTestId from '@ori/testid-generator';
import type { ReactNode, FC } from 'react';
import { useMemo } from 'react';

import { useTranslations } from '../../../../hooks/useTranslations';
import { SocialContactType } from '../../../../models';
import { Contact } from '../Contact';
import {
  TOP_AREA_BANNER_DIALOG,
  TOP_AREA_BANNER_DIALOG_GREETING_MESSAGE,
  TOP_AREA_BANNER_DIALOG_MESSAGE,
} from './constants';
import { BannerDialog, GreetingMessageAndContact, Icon, Root } from './styles';

interface TopAreaBannerProps {
  open: boolean;
  onClose: () => void;
}

const emphasis = (testId: string) => (chunks: ReactNode[]) => <strong data-testid={testId}>{chunks}</strong>;

/**
 * SharingConditionsDialog component is used to render Dialog in How it works module. This dialog contains the sharing conditions.
 */
export const TopAreaBannerDialog: FC<TopAreaBannerProps> = ({ open, onClose }) => {
  const { getTestId } = useTestId();
  const { pbsData } = usePersonalBeautyStore();
  const { translations } = useTranslations();

  const { contacts, referralProgram } = pbsData?.beautyStore?.profile ?? {};
  const { allowedContacts, benefitsLink, greetingMessage, message, title } = referralProgram ?? {};
  const { relativeUrl, title: linkTitle } = benefitsLink ?? {};
  const userEmail = useMemo(
    () => getContactByType(allowedContacts, contacts, SocialContactType.Email),
    [allowedContacts, contacts],
  );
  const userPhone = useMemo(
    () => getContactByType(allowedContacts, contacts, SocialContactType.PhoneNumber),
    [allowedContacts, contacts],
  );

  if (!title || !message) {
    return null;
  }

  return (
    <BannerDialog
      open={open}
      onClose={onClose}
    >
      <Root data-testid={getTestId(TOP_AREA_BANNER_DIALOG)}>
        <Icon />
        <Typography variant="h3">{title}</Typography>
        <Typography variant="body1">
          <DynamicFormattedMessage
            translatedMessage={message}
            values={{
              emphasis: emphasis(getTestId(TOP_AREA_BANNER_DIALOG_MESSAGE)),
            }}
          />
        </Typography>
        <GreetingMessageAndContact>
          <Typography variant="body1">
            {greetingMessage ? (
              <DynamicFormattedMessage
                translatedMessage={greetingMessage}
                values={{
                  emphasis: emphasis(getTestId(TOP_AREA_BANNER_DIALOG_GREETING_MESSAGE)),
                }}
              />
            ) : null}
          </Typography>
          <Contact
            contact={userEmail}
            type="email"
          />
          <Contact
            contact={userPhone}
            type="phone"
          />
        </GreetingMessageAndContact>
        <Button
          fullWidth
          href={relativeUrl == null ? undefined : relativeUrl}
          variant="contained"
          color="primary"
          onClick={relativeUrl == null ? onClose : undefined}
        >
          {linkTitle ?? translations.gotIt}
        </Button>
      </Root>
    </BannerDialog>
  );
};
